import {isFactoryReferenceInUse} from '@dms/api/new-vehicle';

type ErrorEnvelope = {
  errors?: Array<{code?: unknown}>;
};

type ErrorWithStatus = {
  status?: unknown;
  data?: unknown;
};

export type CommitFailure =
  | {kind: 'conflict'}
  | {kind: 'review-conflict'}
  | {kind: 'already-ordered'}
  | {kind: 'factory-reference-in-use'; vehicleOrderId: string; vehicleId: string}
  | {kind: 'different-business-case'}
  | {kind: 'business-case-already-selected'}
  | {kind: 'materialized-outside-customer-commitment'}
  | {kind: 'business-case-state-not-allowed'}
  | {kind: 'business-case-identity-mismatch'}
  | {kind: 'configuration-not-selected'}
  | {kind: 'price-expired'}
  | {kind: 'configuration-not-found'}
  | {kind: 'business-case-not-found'}
  | {kind: 'api-error'}
  | {kind: 'unknown'};

const getErrorCode = (data: unknown) => {
  if (typeof data !== 'object' || data === null || !('errors' in data)) {
    return undefined;
  }
  const errors = (data as ErrorEnvelope).errors;
  const code = errors?.[0]?.code;
  return typeof code === 'string' ? code : undefined;
};

export const getCommitFailure = (error: unknown): CommitFailure => {
  if (typeof error !== 'object' || error === null) {
    return {kind: 'unknown'};
  }

  if (isFactoryReferenceInUse(error)) {
    return {
      kind: 'factory-reference-in-use',
      vehicleOrderId: error.data.vehicleOrderId,
      vehicleId: error.data.vehicleId,
    };
  }

  const candidate = error as ErrorWithStatus;
  const status = candidate.status;
  const code = getErrorCode(candidate.data);

  if (status === 409) {
    switch (code) {
      case 'DMS-NV-CF-0008':
      case 'DMS-NV-CF-0009':
        return {kind: 'conflict'};
      case 'DMS-NV-CF-0002':
        return {kind: 'already-ordered'};
      case 'DMS-NV-CF-0018':
        return {kind: 'different-business-case'};
      case 'DMS-NV-CF-0019':
        return {kind: 'business-case-already-selected'};
      case 'DMS-NV-CF-0020':
        return {kind: 'materialized-outside-customer-commitment'};
      case 'DMS-NV-CF-0010':
      case 'DMS-NV-CF-0012':
        return {kind: 'review-conflict'};
      default:
        return {kind: 'api-error'};
    }
  }

  if (status === 400) {
    switch (code) {
      case 'DMS-NV-CF-0021':
        return {kind: 'business-case-state-not-allowed'};
      case 'DMS-NV-CF-0022':
        return {kind: 'business-case-identity-mismatch'};
      case 'DMS-NV-CF-0023':
        return {kind: 'configuration-not-selected'};
      case 'DMS-NV-CF-0015':
        return {kind: 'price-expired'};
      case 'DMS-NV-CF-0010':
      case 'DMS-NV-CF-0012':
        return {kind: 'review-conflict'};
      default:
        return {kind: 'api-error'};
    }
  }

  if (status === 404) {
    if (code === 'DMS-NV-CF-0007') {
      return {kind: 'configuration-not-found'};
    }
    if (code === 'DMS-NV-CF-0025') {
      return {kind: 'business-case-not-found'};
    }
  }

  return {kind: 'api-error'};
};
